import { Button, Kind } from '@thanx/uikit/button'
import InfoBox from 'components/InfoBox'
import EditNavbar from 'components/Navbar/EditNavbar'
import RichTextEditor from 'components/RichTextEditor'
import Section from 'components/Section'
import { buildTranslate } from 'locales'
import React, { useEffect, useRef } from 'react'
import ReactDOM from 'react-dom'

const t = buildTranslate('cms.checkout_message')

const RECOMMENDED_CHARS = 280

// One below EditNavbar's $zindex-navbar (1000) so the navbar stays on top.
// See src/stylesheets/variables/_bootstrap.scss for the z-index scale.
const OVERLAY_Z_INDEX = 999

type Props = {
  editMessage: string
  maxChars: number
  isSaving: boolean
  isContentEmpty: boolean
  onContentChange: (markdown: string) => void
  onExitWithoutSaving: () => void
  onSave: () => void
}

const EditingOverlay: React.FC<Props> = ({
  editMessage,
  maxChars,
  isSaving,
  isContentEmpty,
  onContentChange,
  onExitWithoutSaving,
  onSave,
}) => {
  const overlayRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    overlayRef.current?.focus()
  }, [])

  return ReactDOM.createPortal(
    <>
      <EditNavbar title={t('title')} className="top-0 mt-0">
        <div>
          <Button
            kind={Kind.SECONDARY_REVERSE}
            className="mr-s"
            onClick={onExitWithoutSaving}
          >
            {t('exit_without_saving')}
          </Button>
          <Button
            kind={Kind.PRIMARY_REVERSE}
            onClick={onSave}
            disabled={isSaving || isContentEmpty}
          >
            {t('save_and_publish')}
          </Button>
        </div>
      </EditNavbar>
      <div
        ref={overlayRef}
        role="dialog"
        aria-modal="true"
        aria-label={t('title')}
        tabIndex={-1}
        className="position-fixed overflow-auto grey-05-bkg"
        style={{
          inset: 0,
          zIndex: OVERLAY_Z_INDEX,
        }}
        onKeyDown={e => {
          if (e.key === 'Escape' && !e.defaultPrevented) onExitWithoutSaving()
        }}
      >
        <div className="px-xl" style={{ paddingTop: '120px' }}>
          <h2 className="mt-0 mb-m">{t('message_heading')}</h2>
          <InfoBox type="tip" content={t('hyperlink_tip')} className="mb-m" />
          <Section className="p-m">
            <label className="bold font-size-14 mb-0 d-block mb-xs">
              {t('message_label')}
            </label>
            <RichTextEditor
              initialContent={editMessage}
              onContentChange={markdown => {
                onContentChange(markdown)
              }}
              maxChars={maxChars}
              placeholder={t('message_placeholder')}
            />
            <div className="mt-xs">
              <span className="font-size-12 grey-70">
                {t('recommended', {
                  max: maxChars,
                  recommend: RECOMMENDED_CHARS,
                })}
              </span>
            </div>
          </Section>
        </div>
      </div>
    </>,
    document.body
  )
}

export default EditingOverlay
